import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerTransactionHistoryInterFace,
  RequestConsumerTransactionHistoryInterFace,
} from '../../../../interface/end-consumer-transaction-history-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchConsumerTransactionHistory,
  fetchConsumerTransactionHistoryFilter,
  setSortData,
  updateConsumerTransactionHistoryListFilter,
} from '../../../../redux/slices/EndConsumerTransactionHistory';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
// import PaymentDetails from '../../../../shared-components/table-side-panel/PaymentDetails';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import TransactionSidePanel from '../../../../shared-components/table-side-panel/TransactionSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
}

/**
 * Charge Session List
 * @returns
 */
function TransactionHistoryList() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerTransactionHistory);
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
  });

  /**
   * Handle open station details
   */
  const handleOpenTransactionDetails = (rowData: ConsumerTransactionHistoryInterFace) => {
    navigate(`?transactionObjectId=${rowData?.chargingTransactionId}`);
    setIsOpen(true);
    setModelDetails({
      title: t('transactionDetails'),
      content: <TransactionSidePanel fetchIdFrom="queryParams" />,
    });
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  /**
   * Fetch Users list function
   */
  const fetchData = (data: RequestConsumerTransactionHistoryInterFace) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerTransactionHistory({
          userCustomId: selectedEndConsumer.userCustomId,
          ...data,
        }),
      );
    }
  };
  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.createdAt)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.createdAt)}</p>
        </div>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: 'chargingTransactionId' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: false,
      /**
       * Rendering device name with hyper link
       * @param currentRow
       * @returns
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenTransactionDetails(currentRow)}>
          {currentRow?.chargingTransactionId || ''}
        </p>
      ),
    },
    {
      label: t('paymentMethod'),
      accessorKey: 'paymentMethod' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: false,
      /**
       * Rendering payment method with translation
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => (
        <p>{currentRow?.paymentMethod ? t(currentRow.paymentMethod) : t('na')}</p>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Rendering amount with currency
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(currentRow?.amount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: false,
      /**
       * status
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => {
        if (currentRow?.status === 'captured') {
          return <p className="success">Success</p>;
        }
        return (
          <p className={`${currentRow?.status}`}>
            {currentRow?.status
              ? currentRow.status.charAt(0).toUpperCase() + currentRow.status.slice(1).toLowerCase()
              : 'NA'}{' '}
          </p>
        );
      },
    },
  ];

  /**
   * Current columns for Transaction History export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('transactionId'), accessorKey: 'chargingTransactionId' },
    { label: t('paymentMethod'), accessorKey: 'paymentMethod' },
    { label: t('amount'), accessorKey: 'amount' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    if (!selectedEndConsumer?.userCustomId) return;

    endConsumerApi
      .transactionExport(selectedEndConsumer.userCustomId, payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerTransactionHistoryInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row?.createdAt
                  ? `${ConvertUTCtoDate(row.createdAt)} ${convertUtcTo12HourTime(row.createdAt)}`
                  : t('na');

              case 'chargingTransactionId':
                return row?.chargingTransactionId || t('na');

              case 'paymentMethod':
                return row?.paymentMethod || t('na');

              case 'amount':
                return row?.amount
                  ? `${ToFixedValue(row.amount)} ${branding.text.currency}`
                  : t('na');

              case 'status':
                return row?.status || t('na');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-transaction-history.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-transaction-history.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('TransactionHistoryV2');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('TransactionHistoryV2', currentId);
  }, [filters, selectedEndConsumer]);

  return (
    <div className="charging-session-wrap" dir={i18n.dir()}>
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
      />{' '}
      <CustomTable
        dataKey="EndConsumerManagement"
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={window.innerHeight >= 700 ? 325 : 327}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerTransactionHistoryListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerTransactionHistoryFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        ReferenceCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="TransactionHistoryV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default TransactionHistoryList;
